import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageContext } from '../context/PageContext';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Clock, AlertTriangle, FileText, MessageSquare, Search, Download, Flag, StickyNote, Share2, EyeOff, Eye, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { applyIssuePolicy, defaultPolicy } from '../lib/issuePolicy';
import { buildLessonspaceRecordingUrl } from '../lib/lessonspace';
import QualityEvidence, { buildQualityDimensions, ratingLabel } from '../components/QualityEvidence';
import {
  getPunctualityVerdict,
  getTutorLatenessSeconds,
  getStudentLatenessSeconds,
  formatLateness,
  DEFAULT_THRESHOLDS,
} from '../lib/punctuality';

const LessonDetail = () => {
  const { sessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Pages that link here (e.g. Issues) pass where to return to.
  const backTo = location.state?.from || '/lessons';
  const backLabel = location.state?.fromLabel || 'Back to lessons';
  const isDark = theme === 'dark';
  const { setSubtitle } = usePageContext();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(null);
  const highlightRef = useRef(null);
  const tParam = searchParams.get('t');

  const parseTimestampToSeconds = (s) => {
    if (s == null) return null;
    const str = String(s).trim();
    if (!str) return null;
    if (/^\d+$/.test(str)) return parseInt(str, 10);
    const parts = str.split(':').map((p) => Number(p));
    if (parts.some((n) => Number.isNaN(n))) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0];
  };
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [policy, setPolicy] = useState(defaultPolicy());
  const [showSuppressed, setShowSuppressed] = useState(false);

  useEffect(() => {
    if (!organizationId || !sessionId) return;
    loadSession();
    return () => setSubtitle('');
  }, [organizationId, sessionId]);

  useEffect(() => {
    if (activeTab !== 'transcript' || !tParam || !session) return;
    let msgs = [];
    if (Array.isArray(session.transcript_messages) && session.transcript_messages.length > 0) msgs = session.transcript_messages;
    else if (Array.isArray(session.raw_transcript)) msgs = session.raw_transcript;
    else if (Array.isArray(session.transcript)) msgs = session.transcript;
    else if (typeof session.raw_transcript === 'string') {
      const rawLines = session.raw_transcript.split('\n').filter((l) => l.trim());
      msgs = rawLines.map((line) => {
        const m = line.match(/^\[([^\]]+)\]\s*\[([^\]]+)\]:\s*(.+)$/);
        return m ? { timestamp: m[1], speaker: m[2], text: m[3] } : null;
      }).filter(Boolean);
      if (msgs.length === 0) { try { const parsed = JSON.parse(session.raw_transcript); if (Array.isArray(parsed)) msgs = parsed; } catch {} }
    }
    if (msgs.length === 0) return;
    const targetSec = parseTimestampToSeconds(tParam);
    if (targetSec == null) return;
    let bestIdx = 0;
    let bestDelta = Infinity;
    msgs.forEach((m, i) => {
      const ts = m.timestamp_seconds ?? parseTimestampToSeconds(m.timestamp);
      if (ts == null) return;
      const delta = Math.abs(ts - targetSec);
      if (delta < bestDelta) { bestDelta = delta; bestIdx = i; }
    });
    setHighlightIdx(bestIdx);
    setTranscriptSearch('');
    setTimeout(() => { highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    const fade = setTimeout(() => setHighlightIdx(null), 4000);
    return () => clearTimeout(fade);
  }, [activeTab, tParam, session]);

  // Hands a MM:SS moment to the existing ?tab=transcript&t=<MM:SS> deep-link
  // mechanism above, which scroll-highlights the nearest transcript line.
  const jumpToTranscript = (timestampDisplay) => {
    if (!timestampDisplay) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'transcript');
    next.set('t', timestampDisplay);
    setSearchParams(next, { replace: true });
    setActiveTab('transcript');
  };

  // Tutor/student attribution: confirm the auto-detected roles, or swap them
  // (a swap re-runs this lesson's analysis with the corrected roles).
  const [rolesBusy, setRolesBusy] = useState(false);
  const setRoles = async (action) => {
    setRolesBusy(true);
    const { error } = await supabase.rpc('set_session_roles', { p_session: session.id, p_action: action });
    setRolesBusy(false);
    if (error) {
      toast.error(error.message === 'not authorised' ? 'Only admins, managers and reviewers can change this' : 'Could not update roles');
      return;
    }
    toast.success(action === 'swap' ? 'Tutor and student swapped — re-analysing this lesson' : 'Tutor and student confirmed');
    loadSession();
  };

  const loadSession = async () => {
    setLoading(true);
    try {
      const [{ data, error }, { data: orgRow }] = await Promise.all([
        supabase
          .from('sessions')
          .select(`
            *,
            risks_v2(*),
            quality_assessments(*),
            session_summaries_v2(*)
          `)
          .eq('id', sessionId)
          .eq('organization_id', organizationId)
          .single(),
        supabase
          .from('organizations')
          .select('lateness_minor_minutes, lateness_moderate_minutes, lateness_severe_minutes, issue_filter_policy')
          .eq('id', organizationId)
          .single(),
      ]);
      if (error) throw error;
      setSession(data);
      if (orgRow) {
        setThresholds({
          minor: orgRow.lateness_minor_minutes ?? DEFAULT_THRESHOLDS.minor,
          moderate: orgRow.lateness_moderate_minutes ?? DEFAULT_THRESHOLDS.moderate,
          severe: orgRow.lateness_severe_minutes ?? DEFAULT_THRESHOLDS.severe,
        });
        setPolicy(orgRow.issue_filter_policy || defaultPolicy());
      }
      if (data) {
        const date = data.lesson_timestamp ? new Date(data.lesson_timestamp).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        setSubtitle(`Lesson between ${data.tutor_name || 'Unknown'} and ${data.student_name || 'Unknown'}${date ? ` on ${date}` : ''}`);
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`${cardClass} h-24 animate-pulse`} />
        <div className={`${cardClass} h-48 animate-pulse`} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(backTo)} className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {backLabel}
        </Button>
        <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Session not found</div>
      </div>
    );
  }

  const qa = Array.isArray(session.quality_assessments) ? session.quality_assessments[0] : session.quality_assessments;
  const summary = Array.isArray(session.session_summaries_v2) ? session.session_summaries_v2[0] : session.session_summaries_v2;
  const risks = Array.isArray(session.risks_v2) ? session.risks_v2 : [];
  const nonLatenessRisks = risks.filter(r => r.category !== 'tutor_lateness');
  const { visible: visibleRisks, suppressed: suppressedRisks } = applyIssuePolicy(nonLatenessRisks, policy);
  const displayedRisks = showSuppressed ? [...visibleRisks, ...suppressedRisks] : visibleRisks;

  const qaInsufficient = !!qa?.insufficient_evidence;
  // Prefers the org-configurable `dimensions` JSONB, falling back to the legacy
  // five *_rating / *_evidence columns. Both yield the same shape.
  const qualityDimensions = buildQualityDimensions(qa);

  const tutorLate = getTutorLatenessSeconds(session);
  const studentLate = getStudentLatenessSeconds(session);
  const punctuality = getPunctualityVerdict(session, thresholds);

  const recordingSource = {
    recordingLink: session.lessonspace_recording_link,
    lessonspaceSessionId: session.lessonspace_session_id,
    sessionExternalId: session.session_id,
  };
  const recordingUrl = buildLessonspaceRecordingUrl(recordingSource);

  // Transcript messages — try multiple sources matching original app
  let transcriptMessages = [];
  if (session.transcript_messages && Array.isArray(session.transcript_messages) && session.transcript_messages.length > 0) {
    transcriptMessages = session.transcript_messages;
  } else if (session.raw_transcript) {
    // Original app format: lines of "[timestamp] [speaker]: text"
    if (typeof session.raw_transcript === 'string') {
      const rawLines = session.raw_transcript.split('\n').filter(l => l.trim());
      transcriptMessages = rawLines.map(line => {
        const m = line.match(/^\[([^\]]+)\]\s*\[([^\]]+)\]:\s*(.+)$/);
        return m ? { timestamp: m[1], speaker: m[2], text: m[3] } : null;
      }).filter(Boolean);
      // If regex didn't match, try JSON parse
      if (transcriptMessages.length === 0) {
        try {
          const parsed = JSON.parse(session.raw_transcript);
          if (Array.isArray(parsed)) transcriptMessages = parsed;
        } catch {}
      }
    } else if (Array.isArray(session.raw_transcript)) {
      transcriptMessages = session.raw_transcript;
    }
  }
  if (transcriptMessages.length === 0 && session.transcript && Array.isArray(session.transcript)) {
    transcriptMessages = session.transcript;
  }

  const filteredTranscript = transcriptSearch
    ? transcriptMessages.filter(m => (m.text || m.message || '').toLowerCase().includes(transcriptSearch.toLowerCase()))
    : transcriptMessages;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'issues', label: `Flagged Issues${visibleRisks.length > 0 ? ` (${visibleRisks.length})` : ''}` },
    { key: 'transcript', label: 'Transcript' },
  ];

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate(backTo)} className={isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}>
        <ArrowLeft className="h-4 w-4 mr-2" /> {backLabel}
      </Button>

      {/* Header Info Bar */}
      <div className={cardClass}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className={`text-xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {session.tutor_name ? (
                <button
                  onClick={() => navigate(`/tutors/${encodeURIComponent(session.tutor_name)}`)}
                  className="hover:underline hover:text-[#586f8d] transition-colors"
                >
                  {session.tutor_name}
                </button>
              ) : '—'}
              {' → '}
              {session.student_name ? (
                <button
                  onClick={() => navigate(`/students/${encodeURIComponent(session.student_name)}`)}
                  className="hover:underline hover:text-[#586f8d] transition-colors"
                >
                  {session.student_name}
                </button>
              ) : '—'}
            </h1>
            <div className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              {session.subject_covered || 'No subject'} · {session.duration_minutes ? `${session.duration_minutes} minutes` : '—'}
            </div>
            <div className={`text-xs mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              {new Date(session.lesson_timestamp).toLocaleString()}
              {session.session_id && <span className="ml-2">· ID: {session.session_id}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {visibleRisks.length > 0 && (
              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {visibleRisks.length} issue{visibleRisks.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {session.speaker_review_needed && (
          <div className={`mb-4 p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center gap-3 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`text-xs flex-1 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
              <strong>Please confirm the tutor.</strong> Lessonspace didn't label this lesson's roles clearly, and the signals from the
              conversation disagree. We've assumed <strong>{session.tutor_name}</strong> is the tutor.
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button disabled={rolesBusy} onClick={() => setRoles('confirm')}
                className={`px-3 py-1.5 text-xs rounded-md border ${isDark ? 'border-[#3f3f46] text-white hover:bg-[#27272a]' : 'border-[#d4d4d8] text-[#1a1a1a] hover:bg-white'} disabled:opacity-50`}>
                Correct
              </button>
              <button disabled={rolesBusy} onClick={() => setRoles('swap')}
                className="px-3 py-1.5 text-xs rounded-md text-white accent-gradient hover:opacity-90 disabled:opacity-50">
                Swap tutor &amp; student
              </button>
            </div>
          </div>
        )}

        {/* Key Info Grid */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Tutor</div>
            {session.tutor_name ? (
              <button
                onClick={() => navigate(`/tutors/${encodeURIComponent(session.tutor_name)}`)}
                className={`text-sm font-medium text-left hover:underline hover:text-[#586f8d] transition-colors ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}
              >
                {session.tutor_name}
              </button>
            ) : (
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>—</div>
            )}
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Student</div>
            {session.student_name ? (
              <button
                onClick={() => navigate(`/students/${encodeURIComponent(session.student_name)}`)}
                className={`text-sm font-medium text-left hover:underline hover:text-[#586f8d] transition-colors ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}
              >
                {session.student_name}
              </button>
            ) : (
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>—</div>
            )}
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Duration</div>
            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{session.duration_minutes ? `${session.duration_minutes}m` : '—'}</div>
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Date</div>
            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{new Date(session.lesson_timestamp).toLocaleDateString()}</div>
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Punctuality</div>
            <div className={`text-sm font-medium ${punctuality.status === 'late' ? 'text-amber-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
              {punctuality.status === 'late'
                ? `${punctuality.who === 'tutor' ? 'Tutor' : 'Student'} ${formatLateness(punctuality.seconds)} late`
                : 'On time'}
            </div>
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Issues</div>
            <div className={`text-sm font-medium ${visibleRisks.length > 0 ? 'text-amber-400' : isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{visibleRisks.length} flagged</div>
          </div>
        </div>
        {!session.speaker_review_needed && (session.tutor_name || session.student_name) && (
          <div className="mt-2 flex justify-end">
            <button disabled={rolesBusy}
              onClick={() => { if (window.confirm('Swap tutor and student for this lesson? Its analysis will re-run with the corrected roles.')) setRoles('swap'); }}
              className={`text-xs underline-offset-2 hover:underline disabled:opacity-50 ${isDark ? 'text-[#71717a] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`}>
              Wrong way round? Swap tutor &amp; student
            </button>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            const text = [
              `Lesson: ${session.tutor_name} → ${session.student_name}`,
              `Date: ${new Date(session.lesson_timestamp).toLocaleString()}`,
              `Subject: ${session.subject_covered || 'N/A'}`,
              `Duration: ${session.duration_minutes || 0} minutes`,
              summary?.session_summary ? `\nSummary:\n${summary.session_summary}` : '',
              qualityDimensions.length > 0 ? `\nQuality:\n${qualityDimensions.map(d => `  ${d.label}: ${ratingLabel(d.rating)}`).join('\n')}` : '',
              qaInsufficient ? '\nQuality: Not assessed — insufficient transcript content' : '',
              visibleRisks.length > 0 ? `\nFlagged Issues: ${visibleRisks.length}` : '',
            ].filter(Boolean).join('\n');
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lesson-report-${session.tutor_name}-${new Date(session.lesson_timestamp).toISOString().slice(0,10)}.txt`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Report downloaded');
          }}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
            isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
          }`}
        >
          <Download className="h-3.5 w-3.5" /> Download Report
        </button>
        <button
          onClick={async () => {
            try {
              await supabase.from('sessions').update({ flagged_for_review: !session.flagged_for_review }).eq('id', session.id);
              setSession(s => ({ ...s, flagged_for_review: !s.flagged_for_review }));
              toast.success(session.flagged_for_review ? 'Flag removed' : 'Flagged for review');
            } catch {
              toast.error('Failed to update flag');
            }
          }}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
            session.flagged_for_review
              ? 'border-amber-500/30 text-amber-400 hover:border-amber-500/50'
              : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
          }`}
        >
          <Flag className="h-3.5 w-3.5" /> {session.flagged_for_review ? 'Flagged' : 'Flag for Review'}
        </button>
        <button
          onClick={() => {
            const note = window.prompt('Add a note for this lesson:');
            if (note !== null && note.trim()) {
              supabase.from('sessions').update({ review_note: note.trim() }).eq('id', session.id)
                .then(() => {
                  setSession(s => ({ ...s, review_note: note.trim() }));
                  toast.success('Note saved');
                })
                .catch(() => toast.error('Failed to save note'));
            }
          }}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
            session.review_note
              ? isDark ? 'border-[#7662ac]/30 text-[#7662ac] hover:border-[#7662ac]/50' : 'border-[#586f8d]/30 text-[#586f8d] hover:border-[#586f8d]/50'
              : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
          }`}
        >
          <StickyNote className="h-3.5 w-3.5" /> {session.review_note ? 'Edit Note' : 'Add Note'}
        </button>
        <button
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied to clipboard');
          }}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
            isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
          }`}
        >
          <Share2 className="h-3.5 w-3.5" /> Share Link
        </button>
        {recordingUrl && (
          <a
            href={recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
              isDark
                ? 'border-[#7662ac]/40 text-[#7662ac] hover:text-white hover:border-[#7662ac]'
                : 'border-[#586f8d]/40 text-[#586f8d] hover:text-[#1a1a1a] hover:border-[#586f8d]'
            }`}
          >
            <PlayCircle className="h-3.5 w-3.5" /> Watch Recording
          </a>
        )}
      </div>

      {/* Review Note Display */}
      {session.review_note && (
        <div className={`rounded-lg border p-3 ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-[#f9f9f9] border-[#e5e5e5]'}`}>
          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>Review Note</div>
          <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{session.review_note}</p>
        </div>
      )}

      {/* Tabs */}
      <div className={`flex gap-6 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`text-sm font-medium pb-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? isDark ? 'text-white border-[#586f8d]' : 'text-[#1a1a1a] border-[#586f8d]'
                : isDark ? 'text-[#52525b] border-transparent hover:text-[#a1a1aa]' : 'text-[#999] border-transparent hover:text-[#666]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Session Summary */}
          {summary?.session_summary && (
            <div className={cardClass}>
              <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Session Summary</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{summary.session_summary}</p>
            </div>
          )}

          {/* Quality Assessment — abstained */}
          {qaInsufficient && (
            <div className={cardClass}>
              <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Teaching Quality Assessment</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                Not assessed — this transcript doesn't contain enough substantive content to fairly evaluate teaching quality. This usually indicates a recording or transcription failure rather than a problem with the tutor.
              </p>
              <p className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                This lesson is excluded from the tutor's quality averages.
              </p>
            </div>
          )}

          {/* Quality Assessment */}
          {qualityDimensions.length > 0 && (
            <div className={cardClass}>
              <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Teaching Quality Assessment</h3>
              <QualityEvidence
                dimensions={qualityDimensions}
                isDark={isDark}
                recording={recordingSource}
                onJumpToTranscript={jumpToTranscript}
              />

              {/* Overall Summary */}
              {qa.overall_summary && (
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                  <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Overall Assessment</div>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{qa.overall_summary}</p>
                </div>
              )}

              {/* Coaching Suggestion */}
              {qa.coaching_suggestion && (
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                  <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>Coaching Suggestion</div>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{qa.coaching_suggestion}</p>
                </div>
              )}
            </div>
          )}

          {(tutorLate > 0 || studentLate > 0) && (
            <div className={cardClass}>
              <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Punctuality</h3>
              <div className="flex gap-6">
                <div>
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Tutor</span>
                  <p className={`text-sm ${punctuality.tutorAboveThreshold ? 'text-amber-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {tutorLate > 0 ? `${formatLateness(tutorLate)} late` : 'On time'}
                  </p>
                </div>
                <div>
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Student</span>
                  <p className={`text-sm ${punctuality.studentAboveThreshold ? 'text-amber-400' : isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {studentLate > 0 ? `${formatLateness(studentLate)} late` : 'On time'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'issues' && (
        <div className="space-y-3">
          {suppressedRisks.length > 0 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowSuppressed(v => !v)}
                className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  showSuppressed
                    ? isDark ? 'border-[#3f3f46] bg-[#18181b] text-white' : 'border-[#d0d0d0] bg-[#f0f0f0] text-[#1a1a1a]'
                    : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'
                }`}
                title={policy?.summary || 'Issue guardrails policy'}
              >
                {showSuppressed ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {showSuppressed ? `Showing ${suppressedRisks.length} suppressed by guardrails` : `${suppressedRisks.length} suppressed by guardrails`}
              </button>
              <button
                onClick={() => navigate('/settings/issue-guardrails')}
                className={`text-xs ${isDark ? 'text-[#586f8d] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
              >
                Manage guardrails →
              </button>
            </div>
          )}
          {displayedRisks.length === 0 ? (
            <div className={`${cardClass} text-center py-12`}>
              <div className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No flagged issues for this session</div>
            </div>
          ) : (
            displayedRisks.map(r => {
              const isSuppressed = !!r._suppressedBy;
              return (
              <div key={r.id} className={`${cardClass} ${isSuppressed ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {(r.category || '').replace(/_/g, ' ')}
                    {isSuppressed && (
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'}`}>
                        suppressed
                      </span>
                    )}
                  </span>
                  {r.timestamp_display && (
                    <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      <Clock className="h-3 w-3" /> {r.timestamp_display}
                    </span>
                  )}
                </div>

                {r.why_flagged && (
                  <p className={`text-sm mb-3 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{r.why_flagged}</p>
                )}

                {r.tutor_quote && (
                  <div className={`p-3 rounded-lg border-l-2 mb-3 ${isDark ? 'bg-[#18181b] border-[#586f8d]' : 'bg-[#f5f5f5] border-[#586f8d]'}`}>
                    <p className={`text-sm italic ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>"{r.tutor_quote}"</p>
                  </div>
                )}

                {r.context && (
                  <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{r.context}</p>
                )}
              </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'transcript' && (
        <div className={cardClass}>
          {/* Search */}
          <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border ${isDark ? 'border-[#27272a] bg-[#18181b]' : 'border-[#e5e5e5] bg-[#f5f5f5]'}`}>
            <Search className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
            <input
              type="text"
              value={transcriptSearch}
              onChange={e => setTranscriptSearch(e.target.value)}
              placeholder="Search transcript..."
              className={`flex-1 text-sm bg-transparent outline-none ${isDark ? 'text-white placeholder-[#52525b]' : 'text-[#1a1a1a] placeholder-[#999]'}`}
            />
          </div>

          {filteredTranscript.length === 0 ? (
            <div className={`text-sm text-center py-8 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              {transcriptMessages.length === 0 ? 'No transcript available' : 'No matching messages'}
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredTranscript.map((msg, idx) => {
                const speaker = msg.speaker || msg.role || 'Unknown';
                const text = msg.text || msg.message || msg.content || '';
                // Match generously: provider speaker labels can be malformed (e.g. names
                // concatenated), so also try the identified speaker and prefix matches.
                const sp = speaker.toLowerCase();
                const tutorName = session.tutor_name?.toLowerCase();
                const identifiedTutor = session.identified_tutor_speaker?.toLowerCase();
                const isTutor = sp.includes('tutor')
                  || sp === tutorName
                  || (!!identifiedTutor && sp === identifiedTutor)
                  || (!!tutorName && sp.startsWith(tutorName))
                  || (!!identifiedTutor && sp.startsWith(identifiedTutor));
                const isHighlight = !transcriptSearch && highlightIdx != null && transcriptMessages[highlightIdx] === msg;
                return (
                  <div
                    key={idx}
                    ref={isHighlight ? highlightRef : null}
                    className={`flex gap-3 rounded-md transition-colors ${isHighlight ? (isDark ? 'bg-[#7662ac]/15 ring-1 ring-[#7662ac]/40 p-2 -m-2' : 'bg-[#586f8d]/10 ring-1 ring-[#586f8d]/30 p-2 -m-2') : ''} ${idx > 0 && !isHighlight ? `pt-3 border-t ${isDark ? 'border-[#1a1a1a]' : 'border-[#f0f0f0]'}` : ''}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-medium ${
                      isTutor ? 'accent-gradient text-white' : isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#e5e5e5] text-[#666]'
                    }`}>
                      {isTutor ? 'T' : 'S'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{speaker}</span>
                        {/* The avatar letter alone is easy to miss when both names
                            are unfamiliar — spell the role out next to the name. */}
                        <span className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full border ${
                          isTutor
                            ? isDark ? 'border-[#7662ac]/40 text-[#7662ac]' : 'border-[#586f8d]/40 text-[#586f8d]'
                            : isDark ? 'border-[#3f3f46] text-[#71717a]' : 'border-[#d4d4d4] text-[#888]'
                        }`}>
                          {isTutor ? 'Tutor' : 'Student'}
                        </span>
                        {msg.timestamp && <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{msg.timestamp}</span>}
                      </div>
                      <p className={`text-sm mt-0.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonDetail;
